import { useRef } from "react";
import { Editor } from "@tiptap/react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Bold, Italic, Underline, Heading1, Heading2,
  List, ListOrdered, CheckSquare, ImageIcon, Paperclip,
} from "lucide-react";

interface NoteEditorToolbarProps {
  editor: Editor | null;
  onImageUpload: (file: File) => Promise<void>;
  onFileAttach: (file: File) => Promise<void>;
}

const NoteEditorToolbar = ({ editor, onImageUpload, onFileAttach }: NoteEditorToolbarProps) => {
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!editor) return null;

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) await onImageUpload(file);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type.startsWith("image/")) {
      await onImageUpload(file);
    } else {
      await onFileAttach(file);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const btnClass = "h-7 w-7 p-0";

  return (
    <div className="flex items-center gap-0.5 flex-wrap px-3 py-1.5 border-b bg-muted/30">
      <Button
        type="button" size="icon" variant={editor.isActive("bold") ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold"
      ><Bold className="h-3.5 w-3.5" /></Button>

      <Button
        type="button" size="icon" variant={editor.isActive("italic") ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic"
      ><Italic className="h-3.5 w-3.5" /></Button>

      <Button
        type="button" size="icon" variant={editor.isActive("underline") ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Underline"
      ><Underline className="h-3.5 w-3.5" /></Button>

      <Separator orientation="vertical" className="h-5 mx-1" />

      <Button
        type="button" size="icon" variant={editor.isActive("heading", { level: 1 }) ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        title="Heading 1"
      ><Heading1 className="h-3.5 w-3.5" /></Button>

      <Button
        type="button" size="icon" variant={editor.isActive("heading", { level: 2 }) ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title="Heading 2"
      ><Heading2 className="h-3.5 w-3.5" /></Button>

      <Separator orientation="vertical" className="h-5 mx-1" />

      <Button
        type="button" size="icon" variant={editor.isActive("bulletList") ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet List"
      ><List className="h-3.5 w-3.5" /></Button>

      <Button
        type="button" size="icon" variant={editor.isActive("orderedList") ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Ordered List"
      ><ListOrdered className="h-3.5 w-3.5" /></Button>

      <Button
        type="button" size="icon" variant={editor.isActive("taskList") ? "default" : "ghost"}
        className={btnClass} onClick={() => editor.chain().focus().toggleTaskList().run()}
        title="Checklist"
      ><CheckSquare className="h-3.5 w-3.5" /></Button>

      <Separator orientation="vertical" className="h-5 mx-1" />

      <Button
        type="button" size="icon" variant="ghost" className={btnClass}
        onClick={() => imageInputRef.current?.click()} title="Insert Image"
      ><ImageIcon className="h-3.5 w-3.5" /></Button>

      <Button
        type="button" size="icon" variant="ghost" className={btnClass}
        onClick={() => fileInputRef.current?.click()} title="Attach File"
      ><Paperclip className="h-3.5 w-3.5" /></Button>

      <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect} />
    </div>
  );
};

export default NoteEditorToolbar;
